import styled from 'styled-components';
import { useTranslation } from '@hooks';
import Link from 'next/link';

import { FooterItem, Logo } from '@components/common';
import { LockIcon } from '@components/icons';
import { Caption2, Text3 } from '@components/styles/Typography';
import { footerFollow, footerHelp, footerFervente } from 'lib/footerItems';
import { useMutation } from '@tanstack/react-query';
import { CommonServices } from '@services';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { newsletterSchema } from '@lib/validationSchemas';
import { useForm } from 'react-hook-form';
import { Notification } from '@components/ui';
import { colors } from 'styles/variables';

const Footer = ({ filled }) => {
    const { t, locale } = useTranslation();

    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);

    const newsletterMutation = useMutation({
        mutationFn: CommonServices.subscribeNewsletter,
        onSuccess: () => {
            setError(false);
            setSuccess(true);
        },
        onError: () => {
            setSuccess(false);
            setError(true);
        },
    });

    const { register, handleSubmit } = useForm({
        resolver: yupResolver(newsletterSchema),
    });

    const onSubmit = (data) => {
        try {
            newsletterMutation.mutate(data.email);
        } catch (error) {
            setSuccess(false);
            setError(true);
        }
    };

    return (
        <FooterWrapper data-testid="footer">
            {success && (
                <Notification
                    type="success"
                    title="Success!"
                    description="Your e-mail address has been sent"
                    alert
                />
            )}

            {error && (
                <Notification
                    type="error"
                    title="Error!"
                    description="Something went wrong"
                    alert
                />
            )}
            <FooterContainer>
                <List>
                    <li>
                        <Logo width="250px" />
                    </li>
                    <li className="homepage-link">
                        <Link href="/" passHref>
                            <Caption2>Homepage</Caption2>
                        </Link>
                    </li>
                </List>
                <FooterSubcontainer>
                    <List>
                        <li>
                            <Caption2>Fervente</Caption2>
                        </li>
                        {footerFervente.map((footerFervente) => (
                            <li key={footerFervente.slug.en}>
                                <FooterItem
                                    slug={footerFervente.slug[locale]}
                                    name={footerFervente.name[locale]}
                                />
                            </li>
                        ))}
                    </List>
                    <List>
                        <li>
                            <Caption2>{t('footerHelp')}</Caption2>
                        </li>
                        {footerHelp.map((footerHelp) => (
                            <li key={footerHelp.slug.en}>
                                <FooterItem
                                    slug={footerHelp.slug}
                                    name={footerHelp.name}
                                    type={footerHelp?.type}
                                />
                            </li>
                        ))}
                    </List>

                    <List>
                        <li>
                            <Caption2>{t('footerSocialMedia')}</Caption2>
                        </li>
                        {footerFollow.map((footerFollow) => (
                            <li key={footerFollow.slug.en}>
                                <Link href={footerFollow.slug[locale]} passHref>
                                    <Caption2>
                                        {footerFollow.name[locale]}
                                    </Caption2>
                                </Link>
                            </li>
                        ))}
                    </List>
                </FooterSubcontainer>

                <List>
                    <li>
                        <Caption2>Newsletter</Caption2>
                    </li>
                    <li>
                        <Caption2>
                            To be informed about innovations <br /> and
                            opportunities about Fervente.
                        </Caption2>

                        <FormWrapper>
                            <input
                                type="email"
                                placeholder="Your Email"
                                {...register('email')}
                                autocomplete="off"
                            />
                            <button
                                type="submit"
                                onClick={handleSubmit(onSubmit)}
                            >
                                SUBMIT
                            </button>
                        </FormWrapper>
                    </li>
                </List>
            </FooterContainer>
            <FooterRightsText desktopHidden>
                <LockIcon filled={filled} /> {t('footerSecureShopping')}
            </FooterRightsText>
            <Text3
                style={{
                    textAlign: 'center',
                    marginTop: '50px',
                }}
            >
                © All Rights Reserved Fervente 2023
            </Text3>
        </FooterWrapper>
    );
};
const FooterWrapper = styled.footer`
    background-color: ${({ theme }) => theme.colors.secondary};
    padding: 50px 0 25px;

    @media (max-width: 1024px) {
        flex-direction: column;
        padding: 20px 30px;
    }
    @media (max-width: 768px) {
        margin-bottom: 50px;
    }
`;

const FooterContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    flex-wrap: nowrap;
    max-width: 1240px;
    width: 100%;
    margin: 0 auto;
    @media (max-width: 768px) {
        max-width: 500px;
        flex-direction: column;
    }
`;

const FooterSubcontainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    margin: 0 auto;

    svg {
        margin-top: 10px;
    }

    li {
    }

    @media (max-width: 1024px) {
        align-items: flex-start;
    }
`;

const List = styled.ul`
    height: 120px;
    padding: 0;
    width: ${(props) => props.width || '100%'};
    min-width: ${(props) => props.width || 'unset'};
    .homepage-link {
        display: none;
    }
    @media (max-width: 1024px) {
        height: 150px;
        width: 100%;
        min-width: unset;
    }
    @media (max-width: 768px) {
        height: auto;
        .homepage-link {
            display: block;
        }
    }
    li {
        width: auto;
        font-family: 'Futura Now Headline Regular';
        margin-right: 20px;
        margin-bottom: 5px;
        @media (max-width: 768px) {
            margin-right: 0;
        }
        :first-child {
            margin-bottom: 10px;
            @media (max-width: 768px) {
                margin-bottom: 10px;
            }
        }
    }

    a {
        text-decoration: none;
        font-size: 12px;
        line-height: normal;
        letter-spacing: 0.3px;
        color: ${colors.dark};
        font-family: 'Futura Now Headline Regular';
        :hover {
            text-decoration: underline;
        }
    }
    @media (max-width: 1024px) {
        min-height: 0;
        margin: 10px 0;
        width: ${(props) => props.Width || '100%'};
        li {
            text-align: center;
            white-space: nowrap;
        }
    }
    @media (max-width: 768px) {
        max-width: none;
    }
`;
const FooterRightsText = styled(Text3)`
    display: ${({ desktopHidden }) => (desktopHidden && 'none') || 'block'};
    text-align: center;

    @media (max-width: 768px) {
        display: block;
    }
`;

const FormWrapper = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    margin-top: 25px;
    width: 100%;
    input {
        border: none;
        border-bottom: 1px solid #cacaca;
        background: transparent;
        color: #000;
        font-size: 10px;
        line-height: 22px;
        letter-spacing: 1px;
        &::placeholder {
            font-family: 'Futura New Headline Regular', sans-serif;
            color: #cacaca;
            font-size: 14px;
            line-height: 22px;
            letter-spacing: 1px;
            margin-bottom: 3px;
        }
    }
    button {
        border: none;
        cursor: pointer;
        background: transparent;
        color: #000;
        position: absolute;
        right: 0;
        bottom: 0;
        font-family: 'Futura Now Headline Medium', sans-serif;
        font-size: 12px;
        line-height: 18px;
        letter-spacing: 1px;
        text-align: left;
    }
    @media (max-width: 1024px) {
        margin-top: 14px;
        input {
            &::placeholder {
                font-size: 10px;
                line-height: 16px;
                letter-spacing: 1px;
                margin-bottom: 2px;
            }
        }
        button {
            font-size: 12px;
            line-height: 18px;
            letter-spacing: 1px;
        }
    }
`;
export default Footer;
